import React from 'react';
import { ArrowRight, CheckCircle2, Heart, Target, Eye, ShieldCheck } from 'lucide-react';
import { business, stats } from '../data/siteData';

export default function About() {
  return (
    <div>
      <PageHeader
        title="About Us"
        subtitle="A family-run construction firm proudly serving Calgary and Alberta since 1988."
        crumb="About"
      />

      {/* Story */}
      <section className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12 items-center">
        <div className="relative">
          <div className="img-zoom rounded-xl overflow-hidden shadow-2xl">
            <img
              src="/images/project-4.jpg"
              alt="MW Stewart team at work"
              className="w-full h-[500px] object-cover"
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="absolute -bottom-6 -right-6 hidden sm:block bg-amber-500 text-slate-900 p-6 rounded-xl shadow-2xl max-w-xs">
            <div className="text-4xl font-black leading-none">1988</div>
            <div className="mt-1 font-bold">Year Established</div>
            <div className="text-xs mt-1">Three generations of craftsmanship</div>
          </div>
        </div>

        <div>
          <div className="text-sm font-bold tracking-[0.25em] text-amber-600 uppercase mb-3">
            Our Story
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 leading-tight">
            Three decades of paving Calgary’s roads, driveways and lots
          </h2>
          <div className="w-16 h-1 bg-amber-500 my-6" />
          <p className="text-slate-600 leading-relaxed">
            MW Stewart Construction began in {business.established} as a small family operation
            committed to one thing: doing the job right. Over 35 years later we have grown into
            Calgary’s trusted family-run paving contractor, while remaining family owned and
            family run.
          </p>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Our specialists in Hot Asphalt Paving, Protective Seal Coating and Hot Rubberized
            Crack Sealing use cutting-edge equipment and decades of hands-on knowledge to deliver
            surfaces that stand the test of time and Canadian winters.
          </p>

          <ul className="mt-7 space-y-3">
            {[
              'Family owned & operated since day one',
              'Specialized crews trained on modern equipment',
              'Projects of all sizes—residential to municipal',
              'Workmanship & materials guaranteed',
            ].map((b) => (
              <li key={b} className="flex items-start gap-3">
                <CheckCircle2 size={20} className="text-amber-500 mt-0.5 shrink-0" />
                <span className="text-slate-700">{b}</span>
              </li>
            ))}
          </ul>

          <a
            href={`tel:${business.phoneRaw}`}
            className="mt-8 inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-bold px-6 py-3 rounded-md transition-colors"
          >
            Work With Us <ArrowRight size={16} />
          </a>
        </div>
      </section>

      {/* Mission / Vision / Values */}
      <section className="bg-slate-50 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-sm font-bold tracking-[0.25em] text-amber-600 uppercase mb-3">
              Our Foundation
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">
              What drives us every day
            </h2>
            <div className="w-16 h-1 bg-amber-500 mx-auto my-5" />
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                Icon: Target,
                title: 'Our Mission',
                text: 'To deliver durable, beautifully finished asphalt surfaces with honest pricing and unmatched craftsmanship—every job, every time.',
              },
              {
                Icon: Eye,
                title: 'Our Vision',
                text: 'To remain Calgary’s most trusted family-run paving contractor by combining old-school work ethic with modern technology.',
              },
              {
                Icon: Heart,
                title: 'Our Values',
                text: 'Integrity. Quality. Safety. Family. We treat every customer’s property as if it were our own.',
              },
            ].map(({ Icon, title, text }) => (
              <div
                key={title}
                className="bg-white rounded-xl p-8 shadow-sm hover:shadow-xl border border-slate-200 hover:border-amber-500 transition-all"
              >
                <div className="w-14 h-14 rounded-md bg-amber-50 flex items-center justify-center mb-5">
                  <Icon className="text-amber-600" size={26} />
                </div>
                <h3 className="text-xl font-bold text-slate-900">{title}</h3>
                <p className="mt-3 text-slate-600 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why choose us */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-sm font-bold tracking-[0.25em] text-amber-600 uppercase mb-3">
              Why Choose Us
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 leading-tight">
              The MW Stewart difference
            </h2>
            <div className="w-16 h-1 bg-amber-500 my-6" />
            <div className="grid sm:grid-cols-2 gap-6">
              {[
                { t: '35+ Years Experience', d: 'Founded in 1988, serving Calgary and Alberta.' },
                { t: 'Family Owned', d: 'A second-generation team that takes pride in every project.' },
                { t: 'Modern Equipment', d: 'Cutting-edge pavers, rollers and sealcoating rigs.' },
                { t: 'Guaranteed Work', d: 'We stand behind every job with a written warranty.' },
              ].map((x) => (
                <div key={x.t} className="flex gap-4">
                  <div className="w-10 h-10 rounded-md bg-amber-500 text-slate-900 flex items-center justify-center shrink-0">
                    <ShieldCheck size={18} />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900">{x.t}</h4>
                    <p className="text-sm text-slate-600 mt-1">{x.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img
              src="/images/project-2.jpg"
              alt="Project example"
              className="rounded-xl shadow-lg w-full h-72 object-cover"
              loading="lazy"
              decoding="async"
            />
            <img
              src="/images/project-3.jpg"
              alt="Project example"
              className="rounded-xl shadow-lg w-full h-72 object-cover mt-8"
              loading="lazy"
              decoding="async"
            />
            <img
              src="/images/project-6.jpg"
              alt="Project example"
              className="rounded-xl shadow-lg w-full h-72 object-cover -mt-8"
              loading="lazy"
              decoding="async"
            />
            <img
              src="/images/project-8.jpg"
              alt="Project example"
              className="rounded-xl shadow-lg w-full h-72 object-cover"
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="bg-amber-500">
        <div className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((s) => (
            <div key={s.label} className="text-center text-slate-900">
              <div className="text-3xl md:text-4xl font-black">{s.value}</div>
              <div className="text-sm font-semibold uppercase tracking-wider mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export function PageHeader({ title, subtitle, crumb }) {
  return (
    <section className="relative bg-slate-900 text-white overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="/images/project-5.jpg"
          alt=""
          className="w-full h-full object-cover opacity-25"
          loading="eager"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/85 to-slate-900/60" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 py-20 lg:py-24">
        <div className="text-xs font-bold tracking-[0.25em] text-amber-400 uppercase">
          Home / {crumb}
        </div>
        <h1 className="mt-3 text-4xl md:text-5xl font-black tracking-tight">{title}</h1>
        {subtitle && (
          <p className="mt-4 text-slate-300 max-w-2xl text-lg">{subtitle}</p>
        )}
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-2 stripe-accent" />
    </section>
  );
}
